import { useDispatch, useSelector } from "react-redux"
import { bindActionCreators } from "redux"
import { State } from "../../../state"
import { Request, useSystemStateHooks } from "../../../modules/system"
import { UIState } from "../state/reducer"
import * as actionCreators from '../state/actionCreators'
import { RoutePath } from "../../routes"
import { DependencyList, EffectCallback, MutableRefObject, useEffect, useRef } from "react"
import { MenuItem, UIPermission } from "../state/types"
import { FieldValue } from "../components/types"

export const useUIState = (): UIState => useSelector((state: State) => state.uiReducer)

export const useUIStateActions = () => bindActionCreators(actionCreators, useDispatch())

export const useUIHooks = () => {
    const { userHasPermission } = useSystemStateHooks()

    const getMenu = (companyName = 'Compañía', request?: Request): MenuItem[] => {
        const canSaveRequests = userHasPermission(UIPermission.SAVE_REQUESTS, request)
        const canSaveEmployees = userHasPermission(UIPermission.SAVE_EMPLOYEES, request)
        const canSaveExpenses = userHasPermission(UIPermission.SAVE_EXPENSES, request)
        const canSaveCashFunds = userHasPermission(UIPermission.SAVE_CASH_FUNDS, request)
        const canSaveChecks = userHasPermission(UIPermission.SAVE_CHECKS, request)
        const canSaveCreditCards = userHasPermission(UIPermission.SAVE_CREDIT_CARDS, request)
        const canReadTransactions = userHasPermission(UIPermission.READ_TRANSACTIONS, request)
        const canSaveExpenseTypes = userHasPermission(UIPermission.SAVE_EXPENSE_TYPES, request)
        const canSaveSuppliers = userHasPermission(UIPermission.SAVE_SUPPLIERS, request)
        const canSavePurchasesSettings = userHasPermission(UIPermission.SAVE_PURCHASES_SETTINGS, request)
        const canSaveSalesSettings = userHasPermission(UIPermission.SAVE_SALES_SETTINGS, request)
        const canSavePaymentTypes = userHasPermission(UIPermission.SAVE_PAYMENT_TYPES, request)
        const canSaveProductPrices = userHasPermission(UIPermission.SAVE_PRODUCT_PRICES, request)
        const canSavePaymentOrders = userHasPermission(UIPermission.SAVE_PAYMENT_ORDERS, request)

        const companyItems: MenuItem[] = [
            {
                id: 'company',
                label: 'Compañía',
                path: RoutePath.COMPANY
            },
            {
                id: 'branches',
                label: 'Sucursales',
                path: RoutePath.BRANCHES
            }            
        ]
        if (canSaveRequests) {
            companyItems.push({
                id: 'requests',
                label: 'Solicitudes de Acceso',
                path: RoutePath.REQUESTS
            })
        }

        const administrationItems: MenuItem[] = []
        if (canSavePaymentOrders) {
            const paymentsItem: MenuItem = {
                id: 'payments',
                label: 'Pagos',
                icon: 'cash-stack',
                children: [
                    {
                        id: 'pending-payments',
                        label: 'Pagos Pendientes',
                        path: RoutePath.PENDING_PAYMENTS
                    },
                    {
                        id: 'payment-orders',
                        label: 'Órdenes de Pago',
                        path: RoutePath.PAYMENT_ORDERS
                    }
                ]
            }
            administrationItems.push(paymentsItem)
        }
        if (canSaveEmployees || canSaveExpenses) {
            const employeesItem: MenuItem = {
                id: 'employees',
                label: 'Empleados',
                icon: 'file-person',
                children: []
            }
            if (canSaveEmployees) {
                employeesItem.children?.push({
                    id: 'employees',
                    label: 'Lista de Empleados',
                    path: RoutePath.EMPLOYEES
                })
            }
            if (canSaveExpenses) {
                employeesItem.children?.push({
                    id: 'salaries',
                    label: 'Sueldos Liquidados',
                    path: RoutePath.SALARIES
                })
            }
            administrationItems.push(employeesItem)
        }
        if (canSaveCashFunds || canSaveChecks || canSaveCreditCards || canReadTransactions) {
            const fundsItem: MenuItem = {
                id: 'funds-menu',
                label: 'Fondos',
                icon: 'wallet2',
                children: []
            }
            if (canSaveCashFunds) {
                fundsItem.children?.push({
                    id: 'cash-funds',
                    label: 'Lista de Cajas',
                    path: RoutePath.CASH_FUNDS
                })
            }
            if (canSaveChecks) {
                fundsItem.children?.push({
                    id: 'checks',
                    label: 'Cheques',
                    path: RoutePath.CHECKS
                })
            }
            if (canSaveCreditCards) {
                fundsItem.children?.push({
                    id: 'credit-cards',
                    label: 'Tarjetas',
                    path: RoutePath.CREDIT_CARDS
                })
            }
            if (canReadTransactions) {
                fundsItem.children?.push({
                    id: 'transactions',
                    label: 'Transacciones',
                    path: RoutePath.TRANSACTIONS
                })
            }
            administrationItems.push(fundsItem)
        }
        if (canSaveExpenses || canSaveExpenseTypes) {
            const expensesItem: MenuItem = {
                id: 'expenses',
                label: 'Gastos',
                icon: 'receipt',
                children: []
            }
            if (canSaveExpenses) {
                expensesItem.children?.push({
                    id: 'expenses',
                    label: 'Lista de Gastos',
                    path: RoutePath.EXPENSES
                })
            }
            if (canSaveExpenseTypes) {
                expensesItem.children?.push({
                    id: 'expense-types',
                    label: 'Tipos de Gasto',
                    path: RoutePath.EXPENSE_TYPES
                })
            }
            if (canSaveExpenses) {
                expensesItem.children?.push({
                    id: 'salaries',
                    label: 'Sueldos Liquidados',
                    path: RoutePath.SALARIES
                })
            }
            if (canSaveSuppliers) {
                expensesItem.children?.push({
                    id: 'expense-suppliers',
                    label: 'Proveedores',
                    path: RoutePath.EXPENSE_SUPPLIERS
                })
            }
            administrationItems.push(expensesItem)
        }
        if (canSavePaymentTypes) {
            administrationItems.push({
                id: 'payment-types',
                label: 'Medios de Pago',
                path: RoutePath.PAYMENT_TYPES
            })
        }
        administrationItems.push({
            id: 'taxes-menu',
            label: 'Impuestos',
            icon: 'coin',
            children: [
                {
                    id: 'taxes',
                    label: 'Configuración',
                    path: RoutePath.TAXES
                }
            ]
        })

        const productsItems: MenuItem[] = [
            {
                id: 'products',
                label: 'Lista de Productos',
                path: RoutePath.PRODUCTS
            },
            {
                id: 'category-list',
                label: 'Categorías',
                path: RoutePath.CATEGORIES
            },
            {
                id: 'product-settings',
                label: 'Configuración',
                path: `${RoutePath.PRODUCTS}/${RoutePath.SETTINGS}`
            }
        ]
        if (canSaveProductPrices) {
            productsItems.splice(1, 0, {
                id: 'import-prices',
                label: 'Actualizar Precios',
                path: `${RoutePath.PRODUCTS}/${RoutePath.PRICES}`
            })
        }

        const purchaseItems: MenuItem[] = [
            {
                id: 'purchases',
                label: 'Órdenes de Compra',
                path: RoutePath.PURCHASES
            },
            {
                id: 'products',
                label: 'Productos',
                icon: 'box-seam',
                children: productsItems
            },
            {
                id: 'suppliers',
                label: 'Proveedores',
                path: RoutePath.SUPPLIERS
            }
        ]
        if (canSavePurchasesSettings) {
            purchaseItems.push({
                id: 'purchase-settings',
                label: 'Configuración',
                path: `${RoutePath.PURCHASES}/${RoutePath.SETTINGS}`
            })
        }

        const saleItems: MenuItem[] = [
            {
                id: 'sales',
                label: 'Órdenes de Venta',
                path: RoutePath.SALES
            },
            {
                id: 'budgets',
                label: 'Presupuestos',
                path: RoutePath.BUDGETS
            },
            {
                id: 'hoards',
                label: 'Lista de Acopios',
                path: RoutePath.HOARDS
            },
            {
                id: 'customer-type-list',
                label: 'Listas de Precios',
                path: RoutePath.CUSTOMER_TYPES
            },
            {
                id: 'customers-menu',
                label: 'Clientes',
                icon: 'people-fill',
                children: [
                    {
                        id: 'credit-accounts',
                        label: 'Cuentas Corrientes',
                        path: RoutePath.CREDIT_ACCOUNTS
                    },
                    {
                        id: 'credit-debts',
                        label: 'Deudores',
                        path: `${RoutePath.CREDIT_ACCOUNTS}/${RoutePath.DEBTS}`
                    },
                    {
                        id: 'customers',
                        label: 'Lista de Clientes',
                        path: RoutePath.CUSTOMERS
                    }
                ]
            }
        ]
        if (canSaveSalesSettings) {
            saleItems.push({
                id: 'sale-settings',
                label: 'Configuración',
                path: `${RoutePath.SALES}/${RoutePath.SETTINGS}`
            })
        }

        return [
            {
                id: 'inicio',
                label: 'Inicio',
                icon: 'house',
                path: RoutePath.INICIO
            },
            {
                id: 'stock',
                label: 'Stock',
                icon: 'boxes',
                children: [
                    {
                        id: 'stock-list',
                        label: 'Existencias',
                        path: RoutePath.STOCK
                    },
                    {
                        id: 'product-list',
                        label: 'Lista de Productos',
                        path: RoutePath.PRODUCTS
                    }
                ]
            },
            {
                id: 'company',
                label: companyName,
                icon: 'building',
                children: companyItems
            },
            {
                id: 'administration',
                label: 'Administración',
                icon: 'person-badge',
                children: administrationItems
            },
            {
                id: 'purchases-menu',
                label: 'Compras',
                icon: 'cart3',
                children: purchaseItems
            },
            {
                id: 'sales-menu',
                label: 'Ventas',
                icon: 'tags',
                children: saleItems
            },
            {
                id: 'logistics',
                label: 'Logística',
                icon: 'truck',
                children: [
                    {
                        id: 'shipment-orders',
                        label: 'Órdenes de Envío',
                        path: RoutePath.SHIPMENT_ORDERS
                    },
                    {
                        id: 'logistics-delivery-notes',
                        label: 'Remitos',
                        path: RoutePath.DELIVERY_NOTES
                    },
                    {
                        id: 'roadmaps',
                        label: 'Hojas de Ruta',
                        path: RoutePath.ROADMAPS
                    },
                    {
                        id: 'drivers',
                        label: 'Conductores',
                        path: RoutePath.DRIVERS
                    },
                    {
                        id: 'vehicles',
                        label: 'Vehículos',
                        path: RoutePath.VEHICLES
                    }
                ]
            },
            {
                id: 'warehouse',
                label: 'Depósito',
                icon: 'stack',
                children: [
                    {
                        id: 'receptions',
                        label: 'Recepción de Compras',
                        path: RoutePath.RECEPTION_ORDERS
                    },
                    {
                        id: 'pickup-orders',
                        label: 'Órdenes de Retiro',
                        path: RoutePath.PICKUP_ORDERS
                    },
                    {
                        id: 'warehouse-delivery-notes',
                        label: 'Remitos',
                        path: RoutePath.DELIVERY_NOTES
                    },
                    {
                        id: 'shipment-preparation',
                        label: 'Preparación de Envíos',
                        path: RoutePath.SHIPMENT_PREPARATION
                    }
                ]
            }
        ]
    }

    const getFilteredMenu = (query: string, companyName?: string, request?: Request): MenuItem[] => {
        const fullMenu = getMenu(companyName, request);
        if (!query.trim()) return fullMenu;

        const normalizeText = (text: string) => 
            text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

        const lowerQuery = normalizeText(query);

        const filter = (items: MenuItem[]): MenuItem[] => {
            return items
                .map(item => ({ ...item }))
                .filter(item => {
                    const normalizedLabel = normalizeText(item.label);
                    const titleMatches = normalizedLabel.includes(lowerQuery);

                    if (titleMatches) return true;

                    if (item.children) {
                        item.children = filter(item.children);
                        return item.children.length > 0;
                    }

                    return false;
                });
        };

        return filter(fullMenu);
    };

    return {
        getMenu,
        getFilteredMenu
    }
}

export const useEffectAfterMount = (effect: EffectCallback, deps?: DependencyList) => {
    const didMount = useRef(false)

    useEffect(() => {
        if (didMount.current) effect()
        else didMount.current = true
    }, deps)
}

export const useOutsideClickHandler = (refs: MutableRefObject<any>[], onClickOutside: () => void) => {
    useEffect(() => {
        const handleClickOutside = (event: any) => {
            const clicked = refs.filter(ref => ref.current?.contains(event.target))
            if (clicked.length === 0) {
                onClickOutside()
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])
}

export const onManualChange = (onChange: (value?: FieldValue) => void) =>
    (value?: FieldValue, manualChange?: boolean) =>
        manualChange && onChange(value)
